import { FC, useCallback, useState } from 'react'
import { Box, Snackbar, Alert as MuiAlert } from '@mui/material'
import FormatsTable from '../forms/FormatsTable'
import { fetchForms, addFormWithFile, updateFormWithFile, deleteForm } from '@/features/documents/formsThunks'
import { useSnackbar } from '@/hooks/useSnackbar'
import { useFormManager } from '@/hooks/useFormManager'
import { Form } from '@/types'
import { Filehelper } from '../Filehelper'
import FormsForm from '../forms/FormsForm'
import { FormsFormValues, INITIAL_FORMS_VALUES } from '../forms/formsForm.constants'
import { validateFileType } from '../forms/useFormsFormHandlers'
import ConfirmDialog from '@/components/common/ConfirmDialog'
import LoadingOverlay from '@/components/common/LoadingOverlay'
import { useAppDispatch } from '@/app/hooks'

const transformFormPayload = (values: FormsFormValues, isEditing: boolean) => {
  const hasNewDocument = values.document instanceof File

  const formData = {
    name: values.name,
    type: values.type,
    docUrl: Filehelper.getFileName(values.document),
  }

  if (isEditing) {
    return {
      id: values.id,
      formData,
      document: hasNewDocument ? (values.document as File) : undefined,
    }
  }
  return {
    formData,
    document: values.document as File,
  }
}

const FormatsManager: FC = () => {
  const snackbar = useSnackbar()
  const dispatch = useAppDispatch()
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [itemToDelete, setItemToDelete] = useState<Form | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const { values, editingId, isSubmitting, setFieldValue, handleEdit, handleReset, handleSubmit, SubmittingOverlay } =
    useFormManager<FormsFormValues>({
      initialValues: INITIAL_FORMS_VALUES,
      onAddThunk: (payload) => addFormWithFile(payload),
      onUpdateThunk: (id, payload) => updateFormWithFile({ ...payload, id }),
      onFetchThunk: fetchForms,
      transformPayload: transformFormPayload,
    })

  const handleEditEvent = useCallback(
    (item: Form) => {
      const mappedItem: FormsFormValues = {
        id: item.id || '',
        name: item.name || '',
        type: item.type || '',
        document: item.docUrl || null,
      }

      handleEdit(mappedItem, [])
      window.scrollTo({ top: 600, behavior: 'smooth' })
    },
    [handleEdit]
  )

  const validateFormData = useCallback((): { valid: boolean; error?: string } => {
    if (!values.name.trim()) {
      return { valid: false, error: 'Por favor ingresa el nombre del formato' }
    }
    if (!values.type.trim()) {
      return { valid: false, error: 'Por favor selecciona el tipo de documento' }
    }
    if (!values.document) {
      return { valid: false, error: 'Por favor sube un documento' }
    }

    if (values.document instanceof File) {
      const fileValidation = validateFileType(values.document, values.type)
      if (!fileValidation.valid) {
        return { valid: false, error: fileValidation.message }
      }
    }
    return { valid: true }
  }, [values.name, values.type, values.document])

  const handleFormatsSubmit = useCallback(
    async (e?: React.FormEvent) => {
      e?.preventDefault()

      const validation = validateFormData()
      if (!validation.valid) {
        snackbar.show(validation.error!, 'error')
        return
      }

      const onSubmit = handleSubmit(
        (isEditing) => {
          snackbar.show(
            isEditing ? 'Formato actualizado correctamente' : 'Formato agregado correctamente',
            'success'
          )
        },
        (errorMsg) => snackbar.show(errorMsg, 'error')
      )

      await onSubmit()
    },
    [handleSubmit, snackbar, validateFormData]
  )

  const handleDeleteClick = useCallback((form: Form) => {
    setItemToDelete(form)
    setDeleteDialogOpen(true)
  }, [])

  const handleDeleteCancel = useCallback(() => {
    setDeleteDialogOpen(false)
    setItemToDelete(null)
  }, [])

  const handleDeleteConfirm = useCallback(async () => {
    if (!itemToDelete?.id) return

    setIsDeleting(true)
    try {
      await dispatch(deleteForm(itemToDelete.id)).unwrap()
      snackbar.show('Formato eliminado correctamente', 'success')
      setDeleteDialogOpen(false)
      setItemToDelete(null)
    } catch (error: any) {
      snackbar.show(error || 'Error al eliminar el formato', 'error')
    } finally {
      setIsDeleting(false)
    }
  }, [itemToDelete, dispatch, snackbar])

  return (
    <Box>
      {SubmittingOverlay()}
      {isDeleting && <LoadingOverlay message="Eliminando formato, por favor espere..." />}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={snackbar.close}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <MuiAlert onClose={snackbar.close} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </MuiAlert>
      </Snackbar>

      <ConfirmDialog
        open={deleteDialogOpen}
        title="Eliminar Formato"
        message="¿Estás seguro de que deseas eliminar el formato"
        itemName={itemToDelete?.name}
        warningMessage="Esta acción eliminará permanentemente el formato y no se puede deshacer."
        confirmText="Eliminar"
        cancelText="Cancelar"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        confirmColor="error"
        isLoading={isDeleting}
      />

      <FormsForm
        values={values}
        editingId={editingId}
        isSubmitting={isSubmitting}
        setFieldValue={setFieldValue}
        onSubmit={handleFormatsSubmit}
        onReset={handleReset}
        onSnackbar={snackbar.show}
      />

      <FormatsTable onEdit={handleEditEvent} onDelete={handleDeleteClick} />
    </Box>
  )
}

export default FormatsManager